import { unparse } from 'papaparse';
import { TableItemsOptions } from '../../types';

export enum TableCode {
	draft = 'form.draft',
	submitted = 'form.pending',
	published = 'form.public',
	members = 'contacts.member',
	volunteers = 'contacts.volunteer',
	eventParticipants = 'contacts.participant',
	jobParticipants = 'contacts.jobParticipant',
	allContacts = 'contacts.all',
	mergeContacts = 'contacts.merge',
	applications = 'contacts.application',
	allTransactions = 'payment.allTransactions',
	allPayments = 'payment.allPayments',
	cashBalance = 'payment.cashBalance',
	planList = 'plan.list',
	partner = 'partnerList.list',
	addPartner = 'addPartnerList.list',
	staffAccount = 'staffAccounts.list',
	ngoAppBannerList = 'ngoAppBanner.list',
	auditLog = 'auditLog.list',
	currentJobList = 'jobs.list.current',
	draftJobList = 'jobs.list.draft',
	allJobList = 'jobs.list.all',
	jobApplicationList = 'jobs.applications.list',
	serviceHourList = 'jobs.hours.list',
	serviceHourSessions = 'jobs.hours.sessions',
	currentEventList = 'events.list.current',
	draftEventList = 'events.list.draft',
	allEventList = 'events.list.all',
	attendanceEventList = 'events.attendance.list',
	attendanceEventScheduleList = 'events.attendance.schedule.list',
	eventApplicationList = 'events.applications.list',
	eventTicketingEventList = 'events.ticketing.event.list',
	eventTicketingRedeemList = 'events.ticketing.redeem.list',
	stripeProductList = 'product.list',
	draftProgramList = 'programs.list.draft',
	publishedProgramList = 'programs.list.published',
	programReportList = 'programs.report.list',
	programEventList = 'programs.event.list',
	programEventJobList = 'programs.event.job.list',
	programEventEventList = 'programs.event.event.list',
	programProposalAndEvaluationList = 'programs.proposalAndEvaluation.list',
	programProposalAndEvaluationMtrList = 'programs.proposalAndEvaluationMtr.list',
	programAdvancePaymentRequestList = 'programs.advancePaymentRequest.list',
	programProposalEventList = 'programs.proposalEvent.list',
}

export const HIDE_COLUMN_ALIGN = ' d-none';

enum QUERY_PARAM {
	SKIP = 'skip=',
	TAKE = '&take=',
	SORT = '&sort=',
	SEARCH = '&search=',
	FILTER = '&filter=',
	TIMEZONE = '&tz=',
}

function skipTake(params: TableItemsOptions): string {
	const skip = (params.page - 1) * params.itemsPerPage;
	const take = params.itemsPerPage;
	return QUERY_PARAM.SKIP + skip + QUERY_PARAM.TAKE + take;
}

function sort(params: TableItemsOptions): string {
	if (params.sortBy && params.sortBy.length > 0) {
		let url: string = QUERY_PARAM.SORT;
		params.sortBy.forEach((fieldName: string, index) => {
			url += `${index > 0 ? ',' : ''}${
				params.sortDesc && params.sortDesc[index] ? '-' : ''
			}${fieldName}`;
		});
		return url;
	}
	return '';
}

function search(params: TableItemsOptions): string {
	if (params.search?.headers?.length > 0) {
		let url: string =
			QUERY_PARAM.SEARCH + encodeURIComponent(params.search.text.trim()) + ':';
		params.search.headers.forEach((fieldName: string, index) => {
			url += `${index > 0 ? ',' : ''}${fieldName}`;
		});
		return url;
	}
	return '';
}

function filter(params: TableItemsOptions): string {
	if (params.filter?.length > 0) {
		const url: string =
			QUERY_PARAM.FILTER +
			params.filter
				.map(f =>
					params.filterParams[f] ? `${f}:${params.filterParams[f]}` : f,
				)
				.join(',');
		return url;
	}
	return '';
}

function timeZone(): string {
	return QUERY_PARAM.TIMEZONE + new Date().getTimezoneOffset().toString();
}

export function tableItemUrl(params: TableItemsOptions): string {
	if (!params.url) throw 'Missing url';
	return (
		params.url +
		(params.url?.includes('?') ? '&' : '?') +
		skipTake(params) +
		sort(params) +
		search(params) +
		filter(params) +
		timeZone()
	);
}

export function downloadDataAsCsvFile(
	filename: string,
	data: any[][],
	fields: string[],
	title?: string,
) {
	const csvData = unparse(
		{ fields, data },
		{
			quotes: false,
			quoteChar: '"',
			escapeChar: '"',
			delimiter: ',',
			header: true,
			newline: '\r\n',
		},
	);
	// Need this BOM for Excel (especially on Windows) to work correctly with non-English(e.g. Chinese) charactors
	// https://stackoverflow.com/a/17879474/6228451
	const BOM = '\uFEFF';
	const blob = new Blob([BOM + (title ? title + '\r\n' : '') + csvData], {
		type: 'text/csv; charset=utf-8',
	});
	const e = document.createEvent('MouseEvents');
	const a = document.createElement('a');
	a.download = `${filename}.csv`;
	a.href = URL.createObjectURL(blob);
	a.rel = 'noopener';
	a.target = '_blank';
	a.dataset.downloadurl = ['text/json', a.download, a.href].join(':');
	e.initEvent('click', true, false);
	a.dispatchEvent(e);
}

export function downloadTextAsCsvFile(filename: string, csvData: string) {
	const contentType = 'text/csv';
	const blob = new Blob([csvData], { type: contentType });
	const e = document.createEvent('MouseEvents');
	const a = document.createElement('a');
	a.download = `${filename}.csv`;
	a.href = URL.createObjectURL(blob);
	a.rel = 'noopener';
	a.target = '_blank';
	a.dataset.downloadurl = ['text/json', a.download, a.href].join(':');
	e.initEvent('click', true, false);
	a.dispatchEvent(e);
}
